/**
 * Sales Agent API service
 */

import apiClient, { ApiResponse } from './client';

export interface TerminologyEntry {
  term: string;
  context?: string;
}

export type TerminologyBank = Record<string, TerminologyEntry[]>;

export interface VoiceSettings {
  voice_id: string;
  speed?: number | null;
  emotion?: string | null;
}

export type LeadCaptureState = 'hidden' | 'optional' | 'required';
export type ConversationControlMode = 'exploratory' | 'balanced' | 'directive';
export type StandaloneContainerType = 'none' | 'gtm';
export type StickyTriggerPlacement =
  | 'top-left'
  | 'top-center'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-center'
  | 'bottom-right';
export interface SalesAgent {
  id: string;
  organization_id: string;
  created_by_user_id: string | null;
  name: string;
  description: string | null;
  is_active: boolean;

  tone: string;
  response_style: string;
  language: string;
  conversation_control_mode: ConversationControlMode;
  custom_instructions: string | null;

  greeting_message: string | null;
  fallback_message: string | null;

  use_active_deck: boolean;
  use_all_videos: boolean;
  use_documentation: boolean;
  auto_suggestions_enabled: boolean;

  // White-label identity fields
  agent_name: string | null;
  company_name: string | null;
  product_description: string | null;
  contact_email: string | null;

  // White-label content section fields
  faq_content: string | null;
  conversation_flow: string | null;
  qualification_principles: string | null;
  guardrails_content: string | null;
  example_interactions: string | null;

  // Additional white-label fields
  booking_instructions: string | null;
  integrations_list: string | null;
  objection_responses: string | null;
  calendar_link: string | null;
  slide_talk_tracks: string | null;
  suggestion_instructions: string | null;

  // Entry modal customization
  entry_modal_logo_url: string | null;
  header_logo_url: string | null;
  entry_modal_title: string | null;
  entry_modal_description: string | null;
  entry_modal_button_text: string | null;
  lead_capture_name: LeadCaptureState;
  lead_capture_email: LeadCaptureState;
  lead_capture_phone: LeadCaptureState;
  lead_capture_business: LeadCaptureState;

  // Browser analytics
  browser_analytics_enabled: boolean;
  standalone_container_type: StandaloneContainerType;
  standalone_container_id: string | null;

  // Background screenshot for content viewer
  background_screenshot_url: string | null;
  share_title: string | null;
  share_description: string | null;
  share_thumbnail_url: string | null;

  // Launcher trigger customization
  sticky_trigger_icon_url: string | null;
  sticky_trigger_text: string | null;
  sticky_trigger_bg_color: string | null;
  sticky_trigger_text_color: string | null;
  sticky_trigger_font_family: string | null;
  sticky_trigger_placement: StickyTriggerPlacement | null;
  sticky_trigger_thumbnail_url: string | null;
  launcher_icon_bg_color: string | null;

  demo_slug: string | null;
  staging_slug: string | null;
  voice_settings: VoiceSettings | null;
  terminology_bank: TerminologyBank | null;

  created_at: string;
  updated_at: string;
}

export interface SalesAgentCreate {
  name: string;
  description?: string;
  is_active?: boolean;
  tone?: string;
  response_style?: string;
  language?: string;
  custom_instructions?: string;
  greeting_message?: string;
  fallback_message?: string;
  use_active_deck?: boolean;
  use_all_videos?: boolean;
  use_documentation?: boolean;
  auto_suggestions_enabled?: boolean;
  // White-label identity fields
  agent_name?: string;
  company_name?: string;
  product_description?: string;
  contact_email?: string;
  // White-label content section fields
  faq_content?: string;
  conversation_flow?: string;
  qualification_principles?: string;
  guardrails_content?: string;
  example_interactions?: string;
  // Additional white-label fields
  booking_instructions?: string;
  integrations_list?: string;
  objection_responses?: string;
  calendar_link?: string;
  slide_talk_tracks?: string;
  // Entry modal customization
  entry_modal_logo_url?: string;
  header_logo_url?: string;
  entry_modal_title?: string;
  entry_modal_description?: string;
  entry_modal_button_text?: string;
  lead_capture_name?: LeadCaptureState;
  lead_capture_email?: LeadCaptureState;
  lead_capture_phone?: LeadCaptureState;
  lead_capture_business?: LeadCaptureState;
  browser_analytics_enabled?: boolean;
  standalone_container_type?: StandaloneContainerType;
  standalone_container_id?: string | null;
  background_screenshot_url?: string;
  share_title?: string | null;
  share_description?: string | null;
  share_thumbnail_url?: string | null;
  // Launcher trigger customization
  sticky_trigger_icon_url?: string;
  sticky_trigger_text?: string;
  sticky_trigger_bg_color?: string;
  sticky_trigger_text_color?: string;
  sticky_trigger_font_family?: string | null;
  sticky_trigger_placement?: StickyTriggerPlacement | null;
  sticky_trigger_thumbnail_url?: string | null;
  launcher_icon_bg_color?: string;
  voice_settings?: VoiceSettings | null;
  terminology_bank?: TerminologyBank | null;
}

export interface SalesAgentUpdate {
  is_active?: boolean;
  conversation_control_mode?: ConversationControlMode;
  use_active_deck?: boolean;
  use_all_videos?: boolean;
  use_documentation?: boolean;
  contact_email?: string;
  calendar_link?: string;
  suggestion_instructions?: string;
  auto_suggestions_enabled?: boolean;
  // Entry modal customization
  entry_modal_logo_url?: string;
  header_logo_url?: string;
  entry_modal_title?: string;
  entry_modal_description?: string;
  entry_modal_button_text?: string;
  lead_capture_name?: LeadCaptureState;
  lead_capture_email?: LeadCaptureState;
  lead_capture_phone?: LeadCaptureState;
  lead_capture_business?: LeadCaptureState;
  browser_analytics_enabled?: boolean;
  standalone_container_type?: StandaloneContainerType;
  standalone_container_id?: string | null;
  background_screenshot_url?: string;
  share_title?: string | null;
  share_description?: string | null;
  share_thumbnail_url?: string | null;
  // Launcher trigger customization
  sticky_trigger_icon_url?: string;
  sticky_trigger_text?: string;
  sticky_trigger_bg_color?: string;
  sticky_trigger_text_color?: string;
  sticky_trigger_font_family?: string | null;
  sticky_trigger_placement?: StickyTriggerPlacement | null;
  sticky_trigger_thumbnail_url?: string | null;
  launcher_icon_bg_color?: string;
  demo_slug?: string;
  staging_slug?: string;
  voice_settings?: VoiceSettings | null;
  terminology_bank?: TerminologyBank | null;
}

export interface ConfigGenerationStatus {
  job_id: string;
  status: 'pending' | 'generating' | 'applying' | 'completed' | 'failed';
  company_website_url: string;
  generated_config: Record<string, string> | null;
  applied_to_agent_id: string | null;
  error_message: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string | null;
}

class SalesAgentService {
  /**
   * Get sales agent for an organization
   */
  async getSalesAgent(organizationId: string): Promise<ApiResponse<SalesAgent>> {
    return apiClient.get<SalesAgent>(`/api/organizations/${organizationId}/sales-agent`);
  }

  /**
   * Create a new sales agent
   */
  async createSalesAgent(
    organizationId: string,
    data: SalesAgentCreate
  ): Promise<ApiResponse<SalesAgent>> {
    return apiClient.post<SalesAgent>(`/api/organizations/${organizationId}/sales-agent`, data);
  }

  /**
   * Update sales agent
   */
  async updateSalesAgent(
    organizationId: string,
    data: SalesAgentUpdate
  ): Promise<ApiResponse<SalesAgent>> {
    return apiClient.put<SalesAgent>(`/api/organizations/${organizationId}/sales-agent`, data);
  }

  /**
   * Delete sales agent
   */
  async deleteSalesAgent(organizationId: string): Promise<ApiResponse<{ success: boolean; message: string }>> {
    return apiClient.delete(`/api/organizations/${organizationId}/sales-agent`);
  }

  /**
   * Start config generation job
   */
  async startConfigGeneration(
    organizationId: string,
    data: { company_website_url: string; presentation_deck_id?: string }
  ): Promise<ApiResponse<{ job_id: string; status: string }>> {
    return apiClient.post(`/api/organizations/${organizationId}/sales-agent/generate-config`, data);
  }

  /**
   * Get config generation job status
   */
  async getConfigGenerationStatus(
    organizationId: string,
    jobId: string
  ): Promise<ApiResponse<ConfigGenerationStatus>> {
    return apiClient.get(`/api/organizations/${organizationId}/sales-agent/generate-config/${jobId}`);
  }

  /**
   * Get distinct countries observed in org sessions (for terminology dropdown)
   */
  async getTerminologyCountries(organizationId: string): Promise<ApiResponse<string[]>> {
    return apiClient.get<string[]>(`/api/organizations/${organizationId}/sales-agent/terminology/countries`);
  }
}

const salesAgentService = new SalesAgentService();
export default salesAgentService;
